import { Empty, Modal } from "antd"
import { EntityTable } from "../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../components/EntityTable/surfaces.ts"
import type { ColumnsType } from "antd/es/table"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../constants/placeholders.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { fetchInvoiceDeliveries } from "../redux/invoices.thunks.ts"
import type { InvoiceDelivery } from "../types/invoices.types.ts"

interface InvoiceDeliveryHistoryModalProps {
  open: boolean
  invoiceId?: string | null
  onClose: () => void
}

export const InvoiceDeliveryHistoryModal = ({
  open,
  invoiceId,
  onClose
}: InvoiceDeliveryHistoryModalProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [rows, setRows] = useState<InvoiceDelivery[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !invoiceId) return
    setLoading(true)
    dispatch(fetchInvoiceDeliveries(invoiceId))
      .unwrap()
      .then((data) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [open, invoiceId, dispatch])

  const columns: ColumnsType<InvoiceDelivery> = [
    {
      title: t("invoices.deliveryHistory.sentAt"),
      dataIndex: "sent_at",
      key: "sent_at",
      render: (value: string | null) => value || empty
    },
    {
      title: t("invoices.deliveryHistory.recipient"),
      dataIndex: "recipient",
      key: "recipient",
      render: (value: string) => value || empty
    },
    {
      title: t("invoices.deliveryHistory.status"),
      dataIndex: "status_display",
      key: "status_display",
      render: (value: string | undefined) => value || empty
    },
    {
      title: t("invoices.deliveryHistory.sentBy"),
      dataIndex: "sent_by_name",
      key: "sent_by_name",
      render: (value: string | null | undefined) => value || empty
    }
  ]

  return (
    <Modal
      open={open}
      title={t("invoices.deliveryHistory.title")}
      footer={null}
      onCancel={onClose}
      destroyOnClose
    >
      <EntityTable<InvoiceDelivery>
        surface={TABLE_SURFACES.invoiceDeliveryHistory}
        tab={DEFAULT_TAB}
        hideConfigButton
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        withOutPagination
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("invoices.deliveryHistory.empty")}
            />
          )
        }}
      />
    </Modal>
  )
}
